import { useCallback, useEffect, useRef, useState } from 'react'
import { PdfRegion } from '@/core/components/modal/PdfRegion'
import {
  Button,
  Callout,
  FormScroll,
  FormSection,
  Icon,
  ModalHead,
  Pill,
  Wizard,
  type ModalSource,
  toastError,
  toastSuccess,
} from 'admination-react'
import { ApiError } from '@/core/api/http'
import { SignaturePad } from '@/core/components/ui/signature-pad'
import { useOpenPersonOverview } from '@/core/hooks/usePhaseNav'
import { useApp } from '@/core/state/appStore'
import {
  readUnterschriften,
  unterschreibe,
  type UnterschriftDokument,
  type Unterschreibende,
} from '../../api/unterschrift'
import { pngBase64 } from '../../api/portal'
import { SheetPdf } from '../teilnehmerakte/staff/akte-blaetter'
import type { StaffParticipant } from '@/core/state/appStore'
import { AUFGABE, type AufgabeEntry } from '../../aufgabe'
import {
  erstberichtAbwesendGegenzeichnen,
  erstberichtNachholen,
  fetchErstberichtAbwesendVorschau,
} from '../../api/erstbericht'
import { getSpalteStand } from '../../api/spalteStand'

const AUFGABEN: readonly AufgabeEntry[] = Object.values(AUFGABE)

const NAME: Record<UnterschriftDokument, string> = { foerderplan: 'Förderplan', erstbericht: 'Erstbericht' }

const SCHRITT: Record<Unterschreibende, { button: string; pad: string }> = {
  teilnehmende: {
    button: 'Teilnehmende/r unterschreibt',
    pad: 'Unterschrift Teilnehmende/r',
  },
  coach: {
    button: 'Coach unterschreibt',
    pad: 'Unterschrift Coach',
  },
}

const text = (n: string) => ({
  laden: `Der ${n} wird geladen …`,
  ladenFehlgeschlagen: `Der ${n} konnte nicht geladen werden.`,
  keinDokument: `Für diese Teilnahme ist kein gespeicherter ${n} vorhanden.`,
  schonUnterschrieben: `Der ${n} ist bereits von beiden unterschrieben.`,
  nichtOffen: `Der ${n} ist gerade nicht zum Unterschreiben offen. Der Stand wird neu geladen.`,
  speichernFehlgeschlagen: 'Die Unterschrift konnte nicht gespeichert werden.',
  naechsterSchrittFehlgeschlagen: `Der ${n} ist unterschrieben. Der nächste Schritt konnte nicht geladen werden.`,
  beideUnterschrieben: `${n} von beiden unterschrieben`,
  abgegeben: `${n} abgegeben. Die Unterschrift der teilnehmenden Person wird nachgeholt.`,
  vorschauFehlgeschlagen: 'Die Vorschau konnte nicht erstellt werden.',
  vorschauLaden: 'Die Vorschau wird erstellt …',
  schliessen: 'Schließen',
  zurueck: 'Zurück zur Unterschrift',
})

const TEXTE = { foerderplan: text(NAME.foerderplan), erstbericht: text(NAME.erstbericht) }

type Stand =
  | { kind: 'laden' }
  | { kind: 'hinweis'; tone: 'error' | 'info'; text: string }
  | {
      kind: 'bereit'
      dokumentId: string
      schritt: Unterschreibende | null
      nachholen: boolean
      fassung: number
    }

export function DokumentUnterschrift({
  dokument,
  participant,
  source,
  ansehen = false,
  onClose,
}: {
  dokument: UnterschriftDokument
  participant: StaffParticipant
  source?: ModalSource
  ansehen?: boolean
  onClose: () => void
}) {
  const { dispatch, reloadDashboard } = useApp()
  const openPerson = useOpenPersonOverview()
  const reloadBoard = useRef(reloadDashboard)
  reloadBoard.current = reloadDashboard
  const [stand, setStand] = useState<Stand>({ kind: 'laden' })
  const [signing, setSigning] = useState(false)
  const [abwesend, setAbwesend] = useState<'aus' | 'vorschau' | 'coach'>('aus')
  const [vorschau, setVorschau] = useState<{ blob: Blob | null; laden: boolean }>({ blob: null, laden: false })
  const fertig = useRef(false)
  const abgegeben = useRef(false)
  const [beendet, setBeendet] = useState(false)
  const offen = useRef(true)
  useEffect(() => {
    offen.current = true
    return () => {
      offen.current = false
    }
  }, [])
  const coachingId = participant.id
  const TEXT = TEXTE[dokument]

  const lesen = useCallback(
    async (fassung: number) => {
      try {
        const gelesen = await readUnterschriften(coachingId, dokument)
        if (!gelesen) {
          setStand({ kind: 'hinweis', tone: 'error', text: TEXT.keinDokument })
        } else if (ansehen) {
          setStand({
            kind: 'bereit',
            dokumentId: gelesen.dokumentId,
            schritt: null,
            nachholen: false,
            fassung,
          })
        } else if (gelesen.teilnehmende && gelesen.coach) {
          setStand({ kind: 'hinweis', tone: 'info', text: TEXT.schonUnterschrieben })
          reloadBoard.current()
        } else {
          const nachholen = dokument === 'erstbericht' && gelesen.coach && !gelesen.teilnehmende
          setStand({
            kind: 'bereit',
            dokumentId: gelesen.dokumentId,
            schritt: gelesen.teilnehmende ? 'coach' : 'teilnehmende',
            nachholen,
            fassung,
          })
        }
      } catch (error) {
        console.error('[dokument-unterschrift] read failed', error)
        setStand({ kind: 'hinweis', tone: 'error', text: TEXT.ladenFehlgeschlagen })
      }
    },
    [coachingId, dokument, ansehen, TEXT],
  )

  useEffect(() => {
    void lesen(0)
  }, [lesen])

  async function unterschreiben(dataUrl: string) {
    if (stand.kind !== 'bereit' || !stand.schritt) return
    const { schritt, fassung } = stand
    try {
      if (stand.nachholen) await erstberichtNachholen(coachingId, pngBase64(dataUrl))
      else await unterschreibe(coachingId, dokument, schritt, pngBase64(dataUrl))
    } catch (error) {
      console.error('[dokument-unterschrift] signature refused', error)
      if (error instanceof ApiError && error.status === 409) {
        toastError(TEXT.nichtOffen)
        setSigning(false)
        void lesen(fassung + 1)
      } else {
        toastError(TEXT.speichernFehlgeschlagen)
      }
      throw error
    }
    if (schritt === 'teilnehmende' && !stand.nachholen) {
      reloadBoard.current()
      setStand({ kind: 'bereit', dokumentId: stand.dokumentId, schritt: 'coach', nachholen: false, fassung: fassung + 1 })
    } else {
      fertig.current = true
    }
  }

  async function abwesendOeffnen() {
    setSigning(false)
    setAbwesend('vorschau')
    setVorschau({ blob: null, laden: true })
    try {
      const blob = await fetchErstberichtAbwesendVorschau(coachingId)
      if (offen.current) setVorschau({ blob, laden: false })
    } catch (error) {
      console.error('[dokument-unterschrift] absence preview failed', error)
      toastError(TEXT.vorschauFehlgeschlagen)
      if (!offen.current) return
      setVorschau({ blob: null, laden: false })
      setAbwesend('aus')
      setSigning(true)
    }
  }

  async function abwesendGegenzeichnen(dataUrl: string) {
    try {
      await erstberichtAbwesendGegenzeichnen(coachingId, pngBase64(dataUrl))
    } catch (error) {
      console.error('[dokument-unterschrift] absence hand-in refused', error)
      if (error instanceof ApiError && error.status === 409) {
        toastError(TEXT.nichtOffen)
        setAbwesend('aus')
        void lesen(stand.kind === 'bereit' ? stand.fassung + 1 : 0)
      } else {
        toastError(TEXT.speichernFehlgeschlagen)
      }
      throw error
    }
    fertig.current = true
    abgegeben.current = true
  }

  async function padGeschlossen() {
    setSigning(false)
    if (abwesend === 'coach' && !fertig.current) {
      setAbwesend('vorschau')
      return
    }
    if (!fertig.current) return
    setBeendet(true)
    reloadDashboard()
    const naechste = await getSpalteStand(coachingId).catch((error: unknown) => {
      console.error('[dokument-unterschrift] column state not read', error)
      toastError(TEXT.naechsterSchrittFehlgeschlagen)
      return null
    })
    if (!offen.current) return
    toastSuccess(abgegeben.current ? TEXT.abgegeben : TEXT.beideUnterschrieben)
    onClose()
    const landing = AUFGABEN.find((e) => e.dbName === naechste?.aufgabe)?.landing
    if (!landing) return
    dispatch({ type: 'OPEN_SCREEN', screen: { kind: landing.modal, tab: landing.tab, coachingId, source: source ?? { kind: 'bar' } } })
  }

  const schritt = stand.kind === 'bereit' && !beendet ? stand.schritt : null
  const nichtAnwesend =
    dokument === 'erstbericht' && stand.kind === 'bereit' && stand.schritt === 'teilnehmende' && !stand.nachholen
  const vorschauStand = abwesend === 'vorschau' && !beendet
  const bar = (
    <ModalHead
      left={{ type: 'title', icon: 'i-person', label: participant.name, onClick: () => openPerson(participant.id, () => dispatch({ type: 'CLOSE' })) }}
      center={<span className="bar-title">{NAME[dokument]}</span>}
      actions={vorschauStand ? (
        <Pill>
          <Button primaryAction variant="primary" disabled={!vorschau.blob} onClick={() => setAbwesend('coach')}>
            Vermerk übernehmen und gegenzeichnen
          </Button>
        </Pill>
      ) : schritt ? (
        <Pill>
          <Button primaryAction variant="primary" data-schritt={schritt} onClick={() => setSigning(true)}>
            <Icon name="i-signature" />
            {SCHRITT[schritt].button}
          </Button>
        </Pill>
      ) : null}
      onClose={vorschauStand ? () => { setAbwesend('aus'); setSigning(true) } : onClose}
      closeLabel={vorschauStand ? TEXT.zurueck : TEXT.schliessen}
    />
  )

  const padStep = abwesend === 'coach' ? 'coach' : schritt
  const overlay = padStep ? (
    <SignaturePad
      open={abwesend === 'coach' || signing}
      title={SCHRITT[padStep].pad}
      successMessage={null}
      onSubmit={abwesend === 'coach' ? abwesendGegenzeichnen : unterschreiben}
      onClose={() => void padGeschlossen()}
      secondaryAction={abwesend !== 'coach' && nichtAnwesend
        ? <Button onClick={() => void abwesendOeffnen()}>Teilnehmende/r nicht anwesend</Button> : undefined}
    />
  ) : null

  return (
    <Wizard onClose={onClose} data-mode="staff" data-dokument-unterschrift={schritt ?? stand.kind} bar={bar} overlay={overlay}>
      {vorschauStand || abwesend === 'coach' ? (
        <PdfRegion
          path={null}
          localBlob={vorschau.blob}
          waiting={vorschau.laden}
          loadingLabel={TEXT.vorschauLaden}
          placeholder={TEXT.vorschauFehlgeschlagen}
        />
      ) : stand.kind === 'bereit' ? (
        <SheetPdf key={`${stand.dokumentId}:${stand.fassung}`} dokumentId={stand.dokumentId} />
      ) : (
        <FormScroll>
          <FormSection>
            {stand.kind === 'laden' ? (
              <Callout tone="info" role="status">
                {TEXT.laden}
              </Callout>
            ) : (
              <Callout tone={stand.tone} role={stand.tone === 'error' ? 'alert' : 'status'}>
                {stand.text}
              </Callout>
            )}
          </FormSection>
        </FormScroll>
      )}
    </Wizard>
  )
}
